import {
  HORARIO_COMERCIAL,
  setorFormSchema,
  type SetorDetalhe,
  type SetorForm,
  type SetorResumo,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, MessagesSquare, Pencil, Plus, Power, Users } from 'lucide-react';
import { useState } from 'react';
import { EditorHorario } from '../componentes/EditorHorario';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';
import { internoApi } from '../lib/interno-api';

const FORM_VAZIO: SetorForm = {
  nome: '',
  descricao: '',
  ativo: true,
  ordem: 0,
  horarioAtendimento: HORARIO_COMERCIAL,
  mensagemForaHorario:
    'Estamos fora do horário de atendimento. Deixe sua mensagem e retornaremos no próximo expediente.',
  limitePadrao: 5,
  distribuicaoAutomatica: true,
  exigeTecnico: false,
  aceitaTecnico: false,
  atende24h: false,
  slaPrimeiraRespostaSeg: 120,
  slaInatividadeSeg: 900,
};

export function Setores() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<{ id: string | null; form: SetorForm } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });

  const salvar = useMutation({
    mutationFn: async ({ id, form }: { id: string | null; form: SetorForm }) =>
      id ? gestaoApi.atualizarSetor(id, form) : gestaoApi.criarSetor(form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['setores'] });
      setEditando(null);
      setErro(null);
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível salvar'),
  });

  const desativar = useMutation({
    mutationFn: (id: string) => gestaoApi.desativarSetor(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['setores'] }),
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível desativar'),
  });

  async function abrirEdicao(setor: SetorResumo) {
    const detalhe: SetorDetalhe = await gestaoApi.detalheSetor(setor.id);
    setEditando({
      id: detalhe.id,
      form: {
        nome: detalhe.nome,
        descricao: detalhe.descricao ?? '',
        ativo: detalhe.ativo,
        ordem: detalhe.ordem,
        horarioAtendimento: detalhe.horarioAtendimento,
        mensagemForaHorario: detalhe.mensagemForaHorario ?? '',
        limitePadrao: detalhe.limitePadrao,
        distribuicaoAutomatica: detalhe.distribuicaoAutomatica,
        exigeTecnico: detalhe.exigeTecnico,
        aceitaTecnico: detalhe.aceitaTecnico,
        atende24h: detalhe.atende24h,
        slaPrimeiraRespostaSeg: detalhe.slaPrimeiraRespostaSeg,
        slaInatividadeSeg: detalhe.slaInatividadeSeg,
      },
    });
    setErro(null);
  }

  function enviar() {
    if (!editando) return;
    const parse = setorFormSchema.safeParse(editando.form);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    salvar.mutate({ id: editando.id, form: parse.data });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Setores</h1>
          <p className="text-sm text-neutral-500">
            Filas de atendimento: horário, limite por atendente e prazos.
          </p>
        </div>
        <Botao
          onClick={() => {
            setEditando({ id: null, form: { ...FORM_VAZIO } });
            setErro(null);
          }}
        >
          <Plus size={16} /> Novo setor
        </Botao>
      </header>

      {erro && !editando && <Aviso tipo="erro">{erro}</Aviso>}
      {setores.isPending && <p className="text-sm text-neutral-500">Carregando…</p>}

      <ul className="grid gap-3 md:grid-cols-2">
        {setores.data?.map((setor) => (
          <li
            key={setor.id}
            className={`rounded-card border p-4 ${
              setor.ativo
                ? 'border-neutral-200 dark:border-neutral-800'
                : 'border-dashed border-neutral-300 opacity-70 dark:border-neutral-700'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">
                  {setor.nome}
                  {!setor.ativo && <span className="ml-2 text-xs text-neutral-500">(inativo)</span>}
                </p>
                {setor.descricao && <p className="text-sm text-neutral-500">{setor.descricao}</p>}
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                  setor.emAtendimento
                    ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                }`}
              >
                {setor.emAtendimento ? 'atendendo agora' : 'fora do horário'}
              </span>
            </div>

            <dl className="mt-3 flex gap-4 text-xs text-neutral-500">
              <div className="flex items-center gap-1">
                <Users size={14} /> {setor.totalAtendentes} na equipe
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} /> até {setor.limitePadrao} por atendente
              </div>
            </dl>

            <div className="mt-3 flex gap-2">
              <Botao variante="secundario" onClick={() => void abrirEdicao(setor)}>
                <Pencil size={14} /> Editar
              </Botao>
              {/* O grupo da equipe do setor na conversa interna. Quem está no setor entra
                  sozinho, e sai sozinho ao sair do setor — ninguém precisa convidar. */}
              <Botao
                variante="secundario"
                onClick={() => {
                  void internoApi
                    .grupoDoSetor(setor.id)
                    .then((g) =>
                      alert(
                        `Grupo "${g.nome}" pronto, com ${g.membros} pessoa(s) do setor. Ele aparece em Conversas para quem é da equipe daqui.`,
                      ),
                    )
                    .catch(() => alert('Não consegui criar o grupo da equipe.'));
                }}
              >
                <MessagesSquare size={14} /> Grupo da equipe
              </Botao>
              {setor.ativo && (
                <Botao
                  variante="secundario"
                  onClick={() => {
                    if (confirm(`Desativar o setor ${setor.nome}?`)) desativar.mutate(setor.id);
                  }}
                >
                  <Power size={14} /> Desativar
                </Botao>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Modal
        aberto={!!editando}
        titulo={editando?.id ? 'Editar setor' : 'Novo setor'}
        largura="larga"
        onFechar={() => setEditando(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
            <Botao onClick={enviar} carregando={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        {editando && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
            noValidate
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto
                rotulo="Nome"
                autoFocus
                value={editando.form.nome}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, nome: e.target.value } })
                }
              />
              <CampoTexto
                rotulo="Ordem na lista"
                type="number"
                min={0}
                value={editando.form.ordem}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, ordem: Number(e.target.value) },
                  })
                }
                ajuda="Menor número aparece primeiro para o cliente."
              />
            </div>

            <CampoTexto
              rotulo="Descrição"
              value={editando.form.descricao ?? ''}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, descricao: e.target.value } })
              }
            />

            <EditorHorario
              valor={editando.form.horarioAtendimento}
              onChange={(horarioAtendimento) =>
                setEditando({ ...editando, form: { ...editando.form, horarioAtendimento } })
              }
            />

            <label className="block text-sm font-medium">
              Mensagem fora do horário
              <textarea
                rows={2}
                value={editando.form.mensagemForaHorario ?? ''}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, mensagemForaHorario: e.target.value },
                  })
                }
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={editando.form.distribuicaoAutomatica}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, distribuicaoAutomatica: e.target.checked },
                  })
                }
              />
              <span>
                Distribuir a fila automaticamente
                <span className="block text-xs font-normal text-neutral-500">
                  Desligado, ninguém recebe sozinho: os atendentes puxam da fila quando quiserem. O
                  supervisor do setor também liga e desliga isso na tela de Atendimentos.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={editando.form.atende24h}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, atende24h: e.target.checked },
                  })
                }
              />
              <span>
                Atende 24 horas
                <span className="block text-xs font-normal text-neutral-500">
                  Recolhe o que chega fora do horário dos outros setores. Com isto ligado em algum
                  setor, ninguém mais ouve &quot;volte no próximo expediente&quot;: o cliente é
                  passado para cá. Marque em um setor só.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={editando.form.exigeTecnico}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, exigeTecnico: e.target.checked },
                  })
                }
              />
              <span>
                Só para técnicos cadastrados
                <span className="block text-xs font-normal text-neutral-500">
                  É o caso da Liberação de sinal. Quem digitar a palavra-chave sem ter entrado com a
                  conta de técnico recebe o aviso e continua na triagem, em vez de esperar numa fila
                  que ninguém vai atender.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={editando.form.aceitaTecnico}
                disabled={editando.form.exigeTecnico}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, aceitaTecnico: e.target.checked },
                  })
                }
              />
              <span>
                Também atende técnicos
                <span className="block text-xs font-normal text-neutral-500">
                  O setor continua recebendo cliente pela triagem e passa a aparecer na lista do app
                  do técnico, ao lado da Liberação. É o caso de Rotas, que fala com o cliente sobre
                  a visita e com o técnico sobre a O.S. Setor só para técnicos já atende os dois.
                </span>
              </span>
            </label>

            <div className="grid gap-4 sm:grid-cols-3">
              <CampoTexto
                rotulo="Conversas por atendente"
                type="number"
                min={1}
                max={50}
                value={editando.form.limitePadrao}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, limitePadrao: Number(e.target.value) },
                  })
                }
              />
              <CampoTexto
                rotulo="SLA 1ª resposta (seg)"
                type="number"
                min={30}
                value={editando.form.slaPrimeiraRespostaSeg ?? ''}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: {
                      ...editando.form,
                      slaPrimeiraRespostaSeg: e.target.value ? Number(e.target.value) : null,
                    },
                  })
                }
                ajuda="Vazio = sem prazo"
              />
              <CampoTexto
                rotulo="Inatividade (seg)"
                type="number"
                min={60}
                value={editando.form.slaInatividadeSeg ?? ''}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: {
                      ...editando.form,
                      slaInatividadeSeg: e.target.value ? Number(e.target.value) : null,
                    },
                  })
                }
                ajuda="Silêncio que acende o sinal no Monitor e dispara alerta. Não encerra nada: atendimento em andamento só fecha quando alguém fecha."
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editando.form.ativo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, ativo: e.target.checked } })
                }
              />
              Setor ativo (aparece para o cliente)
            </label>

            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </form>
        )}
      </Modal>
    </div>
  );
}
